// Pre-designer summary. A compact intro panel shown at the very top of the
// customiser, before the customer opens the editor (no saved spots, no
// open draft). Sets expectations upfront — how the flow works, turnaround,
// what the price covers, and care — which reduces drop-off once the
// customer commits to designing.
//
// Copy varies by customiser flow (logo / pet / kid): the standard logo
// flow asks for text or artwork, the pet flow asks for a pet photo, the
// kid flow asks for a child's drawing. Step numbers use the RK palette
// (pink / green / blue), black-outlined, per the brand reference.

import type { Config } from "../types";
import { flowKindFor } from "../flow";
import { computeShipBy } from "../shipBy";

type Step = { title: string; body: string };
type FlowCopy = { heading: string; steps: [Step, Step, Step] };

const CHOOSE_SPOT: Step = {
  title: "Choose a spot",
  body: "Where does the embroidery go on the garment?",
};
const PICK_THREADS: Step = {
  title: "Pick your thread colour",
  body: "Choose from a variety of thread colours",
};

const COPY_BY_FLOW: Record<"logo" | "pet" | "kid" | "person", FlowCopy> = {
  logo: {
    heading: "Design your embroidery in 3 easy steps",
    steps: [
      CHOOSE_SPOT,
      {
        title: "Add your design",
        body: "Type a name, date, phrase, or upload a design",
      },
      PICK_THREADS,
    ],
  },
  pet: {
    heading: "Get your pet embroidered in 3 easy steps",
    steps: [
      CHOOSE_SPOT,
      {
        title: "Upload a picture of your pet",
        body: "Any kind of pet, bird, dog, cat or horse.",
      },
      PICK_THREADS,
    ],
  },
  kid: {
    heading: "Get a kid's drawing embroidered in 3 easy steps",
    steps: [
      CHOOSE_SPOT,
      {
        title: "Upload your kid's drawing",
        body: "Any doodle, sketch or full-blown masterpiece.",
      },
      PICK_THREADS,
    ],
  },
  person: {
    heading: "Get a portrait embroidered in 3 easy steps",
    steps: [
      CHOOSE_SPOT,
      {
        title: "Upload a photo of them",
        body: "One person or a group. We hand-trace it into a line drawing.",
      },
      PICK_THREADS,
    ],
  },
};

export function PreDesignerSummary({ config }: { config: Config }) {
  // Phase 70: read the flow kind from the bound FlowBlueprint (falls
  // back to the legacy template column during migration).
  const flow = flowKindFor(config);
  const copy = COPY_BY_FLOW[flow] ?? COPY_BY_FLOW.logo;
  const shipBy = computeShipBy("standard").label;
  return (
    <section className="rk-pc-intro" aria-label="How customising works">
      <h3 className="rk-pc-intro__title">{copy.heading}</h3>
      <ol className="rk-pc-intro__steps">
        {copy.steps.map((s, i) => (
          <li key={i}>
            <span className="rk-pc-intro__num">{i + 1}</span>
            <div>
              <strong>{s.title}</strong>
              <span>{s.body}</span>
            </div>
          </li>
        ))}
      </ol>
      <p className="rk-pc-intro__shipby">
        <span aria-hidden="true">🧵</span> Made to order in Melbourne. Order before
        midday and we’ll post it by <strong>{shipBy}</strong>. In a hurry? Rush
        production posts within 5 business days.
      </p>
    </section>
  );
}
